import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Company, ContentProvider, Pagination, KnowledgeSkill } from '@lighty';
import { EmitterService, LoaderService, ParamsService, StorageService, TranslateService } from '@services';
import { SubscriptionCollection } from '@classes';
import Glide from '@glidejs/glide';
import { DataHelper } from '@helpers';
import * as moment from 'moment';
import SwiperCore from 'swiper';

@Component({
    selector: 'msc-learn-catalogue-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss']
})
export class LearnCatalogueListComponent implements OnInit, OnDestroy {
    private subscriptionCollection: SubscriptionCollection = new SubscriptionCollection();
    private glide: any;
    private me: any;
    public company: Company;
    public background: string = 'https://static.myskillcamp.com/lxp/images/hero-catalog.png';
    public featured: any;
    public contents: any;
    public externals: any;
    public events: any;
    public contentsCollection: any;
    public thematics: any;
    public providers: any;
    public catalogue: any = [];
    public pagination: Pagination;
    public paginationEvent: Pagination;
    public mode: string = 'list';
    public query: string;
    public filters: any = {};
    public activeFilters: boolean = false;
    public searching: boolean = false;
    public isSearching: boolean = false;
    public loading: boolean = false;
    public loaders: any = {
        list: false,
        calendar: false,
    };
    public action: any;
    public glideView: number = 2;
    public modalOn: boolean = false;
    public cardParams: any;
    public buildFilters: EventEmitter<any> = new EventEmitter();
    public searchTerm: string = '';
    // Propiedades para el carrusel de filtros
    public availableTypes: any[] = [];
    public availableLanguages: any[] = [];
    public availableProviders: any[];
    public filtersModal: any;
    private availableSkills: KnowledgeSkill[];
    // Control de botones de navegación del carrusel
    public canScrollLeft: boolean = false;
    public canScrollRight: boolean = false;

    constructor(private route: ActivatedRoute, private router: Router, private storageService: StorageService, private contentProvider: ContentProvider,
        private loaderService: LoaderService, private emitterService: EmitterService, private paramsService: ParamsService, private translateService: TranslateService) { }

    ngOnInit(): void {
        this.me = this.storageService.get('me');
        this.company = this.storageService.get('company');
        this.background = this.company.experience && this.company.experience.cataloguePicture ? this.company.experience.cataloguePicture + '?size=raw' : this.background;
        this.featured = this.route.snapshot.data.catalogue.featured;
        this.contents = this.route.snapshot.data.catalogue.contents;
        this.externals = this.route.snapshot.data.catalogue.externals;

        this.thematics = {
            contents: [],
            externals: []
        };

        this.providers = {
            internals: [],
            externals: []
        };

        this.action = {
            back: false,
            next: true,
            index: 0
        };

        this.cardParams = {
            catalogue: true
        };

        this.glideView = window.outerWidth > 425 ? this.glideView : 1;
        if (this.featured.length > this.glideView) {
            this.glideInit();
        }

        this.getEvents();
        this.getThematics();

    // Cargar metadatos para los filtros del carrusel
    this.getMeta();

        this.subscriptionCollection.subscribe = this.emitterService.get('search.catalogue').subscribe((query) => {
            if (typeof query === 'string') {
                const timeOut = setTimeout(() => {
                    this.isSearching = true;
                    clearTimeout(timeOut);
                }, 200);
                this.search(query);
                this.emitterService.set('search.catalogue', {});
            }
        });

        this.subscriptionCollection.subscribe = this.emitterService.get('availableProviders').subscribe((data) => {
            if (Array.isArray(data)) {
                this.providers.internals = data.filter((provider) => {
                    return provider.type === 'internal';
                });

                this.providers.externals = data.filter((provider) => {
                    return provider.type === 'external';
                });
            }
        });

        this.subscriptionCollection.subscribe = this.emitterService.get('catalogue.modal').subscribe((data) => {
            if (typeof data === 'boolean') {
                this.modalOn = data;
            }
        });

        // AJUSTE 2: Suscribirse al evento de reset del topbar-filters
        this.subscriptionCollection.subscribe = this.emitterService.get('filter.reset.carousel').subscribe(() => {
            this.resetCarouselFilters();
            this.search();
        });
    }

    ngOnDestroy(): void {
        this.subscriptionCollection.unsubscribe();
    }

    // Cargar metadatos para los filtros del carrusel
    private getMeta(): void {
        this.contentProvider.getMeta('catalogue').subscribe((meta) => {
            this.availableSkills = meta.contentSkills;
            this.availableTypes = Object.keys(meta.contentTypes)
                .map((type) => ({
                    id: `type-${type}`,
                    label: this.translateService.instant(`api.content-type.${type.split(/(?=[A-Z])/).join('_').toLowerCase()}`),
                    value: type.split(/(?=[A-Z])/).join('_').toLowerCase(),
                    isSelected: false,
                    children: []
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            this.availableLanguages = this.getLanguagesOptions(meta.languages);
            this.availableProviders = this.getProvidersOptions(meta.providers);
            this.setFilters();

            // Inicializar botones de navegación
            setTimeout(() => this.updateScrollButtons(), 300);
        });
    }

    private getProvidersOptions(providers) {
        const label = this.company?.experience
            ? (this.company.experience.displayName ? this.company.experience.displayName : this.company.experience.name)
            : this.company?.name;

        return providers
            .filter(provider => provider && (provider.id === 'company' || provider.name))
            .map(provider => ({
                label: provider.id === 'company' ? label : provider.name,
                value: provider.id,
                isSelected: false
            }));
    }

    private getLanguagesOptions(languages) {
        const languageIds = this.filters?.langs ? this.filters.langs : this.me?.learningLanguages?.map(lang => lang.id) || [];

        return languages.map((lang, idx) => ({
            id: idx,
            label: lang.id === 'undefined' ? this.translateService.instant('words.multilingual') : lang.name,
            value: lang.id,
            isSelected: languageIds.includes(lang.id)
        }));
    }

    private setFilters(): void {
        this.filtersModal = [
            {
                name: 'langs',
                type: 'multiselect',
                label: this.translateService.instant('words.languages'),
                displayValue: this.availableLanguages.filter(lang => lang.isSelected).map(lang => lang.label).join(', '),
                options: this.availableLanguages
            },
            {
                name: 'providers',
                type: 'checkbox',
                label: this.translateService.instant('words.providers'),
                options: this.availableProviders
            }
        ];
    }

    private getEvents(): void {
        this.loaders.calendar = true;
        this.contentProvider.getEventsCatalogue().subscribe((data) => {
            this.paginationEvent = data.pagination;
            this.contentsCollection = data.contents;

            this.groupEvents(data);
        });
    }

    private groupEvents(data): void {
        for (const content of data.contents) {
            content.eventDate = moment(content.event.startAt).format('YYYY-MM-DD');
        }
        this.events = DataHelper.groupBy(this.contentsCollection, 'eventDate', { key: 'date', value: 'items' });
        this.loaders.calendar = false;
    }

    private getThematics(): void {
        this.contentProvider.getThematics(this.company.id).subscribe((data) => {
            this.thematics.contents = data[0].skills;
            this.thematics.externals = data[1].skills;
        });
    }

    private glideInit(): void {
        const timeOut = setTimeout(() => {
            if (!document.querySelector('#slider')) { return; }
            this.glide = new Glide('#slider', {
                perView: this.glideView
            });

            this.glide.on('run', (move) => {
                if (move.direction === '=') {
                    this.action.index = parseInt(move.steps, 10);
                }

                if (move.direction === '>') {
                    this.action.index++;
                }

                if (move.direction === '<') {
                    this.action.index--;
                }

                this.action.back = this.action.index > 0;
                this.action.next = this.action.index < this.featured.length - this.glideView;
            });

            this.glide.mount();

            clearTimeout(timeOut);
        }, 50);
    }

    changeMode(): void {
        this.mode = this.mode === 'list' ? 'calendar' : 'list';
    }

    search(query?: string): void {
        console.log('search', query);
        this.searchTerm = query;
        if (this.activeFilters || !!this.query || query) {
            this.searching = true;
            this.loaderService.run();

                    //  Guardamos en localStorage
            const searchQuery = this.query || query;
            localStorage.setItem('lastSearchQuery', searchQuery);

            const params = this.paramsService.getCatalogue(this.filters, this.company.id, this.query || query, null, null, this.me);

            this.contentProvider.getCatalogue(params).subscribe((data) => {
                this.pagination = data.pagination;
                this.catalogue = data.catalogue;
                this.isSearching = true;
                this.searching = false;
            });
        } else {
            this.isSearching = false;
            this.glideInit();
        }
    }



    loadMore(): void {
        this.loading = true;
        this.loaderService.run();

        const params = this.paramsService.getCatalogue(this.filters, this.company.id, null, this.pagination, null, this.me);

        this.contentProvider.getCatalogue(params).subscribe((data) => {
            this.catalogue = this.catalogue.concat(data.catalogue);
            this.pagination = data.pagination;
            this.loading = false;
        });
    }

    loadMoreEvents(): void {
        const params = {
            page: this.paginationEvent.currentPage + 1
        };

        this.loaders.calendar = true;

        this.contentProvider.getEventsCatalogue(params).subscribe((data) => {
            this.paginationEvent = data.pagination;
            this.contentsCollection = this.contentsCollection.concat(data.contents);

            this.groupEvents(data);
        });
    }

    change(): void {
        if (this.mode === 'list' && this.featured.length > 2) {
            this.glideInit();
        }
    }

    details(content: any): void {
        this.router.navigate(['learn', this.company.slug, 'detail', content.type === 'traject' ? 'journey' : 'template', content.id]);
    }

    translate(provider: any): string {
        if (provider.name === 'company') {
            return this.company.name;
        } else if (!isNaN(parseInt(provider.id, 10))) {
            return provider.name;
        }
        return 'words.' + provider.name;
    }

    filter(provider: any): void {
        const params = {
            company: false
        };
        params[provider.id] = true;

        const filter = {
            providers: params
        };

        this.buildFilters.emit(filter);
    }

    // Métodos para el carrusel de filtros
    public scrollCategoriesLeft(): void {
        const container = document.getElementById('category-container');
        if (container) {
            container.scrollBy({ left: -200, behavior: 'smooth' });
            // Actualizar estado después de un delay
            setTimeout(() => this.updateScrollButtons(), 300);
        }
    }

    public scrollCategoriesRight(): void {
        const container = document.getElementById('category-container');
        if (container) {
            container.scrollBy({ left: 200, behavior: 'smooth' });
            // Actualizar estado después de un delay
            setTimeout(() => this.updateScrollButtons(), 300);
        }
    }

    public onCategoryContainerScroll(): void {
        this.updateScrollButtons();
    }

    private updateScrollButtons(): void {
        const container = document.getElementById('category-container');
        if (container) {
            // Verificar si hay contenido que desplazar hacia la izquierda
            this.canScrollLeft = container.scrollLeft > 0;
            
            // Verificar si hay contenido que desplazar hacia la derecha
            this.canScrollRight = container.scrollLeft + container.clientWidth < container.scrollWidth;
        }
    }

    // Métodos adicionales para integración con filtros
    public onSelectType(option: any): void {
        option.isSelected = !option.isSelected;
        
        // AJUSTE 1: Limpieza bidireccional
        // Si hay tipos seleccionados → limpiar proveedores
        const hasSelectedTypes = this.availableTypes.some(type => type.isSelected);
        if (hasSelectedTypes) {
            this.clearProvidersFilter();
        }
        
        this.saveFilters();
    }

    public onInlineMultiselectChange(option: any, filterName: string, optionsArray: any[]): void {
        option.isSelected = !option.isSelected;
        const selectedValues = optionsArray.filter(opt => opt.isSelected).map(opt => opt.id || opt.value);
        this.filters[filterName] = selectedValues.length > 0 ? selectedValues : [];

        // AJUSTE 1: Limpieza bidireccional
        // Si filterName es 'providers' y hay proveedores seleccionados → limpiar tipos de contenido
        if (filterName === 'providers' && selectedValues.length > 0) {
            this.clearContentTypesFilter();
        }

        this.saveFilters();
    }

    private saveFilters(): void {
        // Consolidar filtros de tipos (usando el valor sin el prefijo)
        const selectedTypes = this.availableTypes.filter(type => type.isSelected).map(type => type.value);
        this.filters.contentTypes = selectedTypes.length > 0 ? selectedTypes : [];
        
        // Consolidar filtros de proveedores
        const selectedProviders = this.availableProviders.filter(p => p.isSelected).map(p => p.value);
        this.filters.providers = selectedProviders.length > 0 ? selectedProviders : [];
        
        this.search();
    }

    // AJUSTE 1: Métodos para limpiar filtros cruzados
    private clearProvidersFilter(): void {
        const clearedProviders = this.availableProviders.map(provider => ({
            ...provider,
            isSelected: false
        }));
        this.availableProviders = [...clearedProviders];
        this.filters = DataHelper.merge(this.filters, { providers: [] });
    }

    private clearContentTypesFilter(): void {
        const clearedTypes = this.availableTypes.map(type => ({
            ...type,
            isSelected: false
        }));
        this.availableTypes = [...clearedTypes];
        this.filters = DataHelper.merge(this.filters, { types: [] });
    }

    // AJUSTE 2: Método para resetear filtros del carrusel
    public resetCarouselFilters(): void {
        // Resetear tipos de contenido
        this.availableTypes = this.availableTypes.map(type => ({
            ...type,
            isSelected: false
        }));

        // Resetear proveedores
        this.availableProviders = this.availableProviders.map(provider => ({
            ...provider,
            isSelected: false
        }));

        // Actualizar también en filtersModal si existe
        if (this.filtersModal) {
            this.filtersModal = this.filtersModal.map(filter => {
                if (filter.type === 'checkbox' && filter.options.length) {
                    filter.options = filter.options.map(option => ({
                        ...option,
                        isSelected: false
                    }));
                }
                return filter;
            });
        }

        // Limpiar los filtros aplicados
        this.filters = {
            contentTypes: [],
            providers: []
        };
    }
}
